import { computed } from 'mobx';
import { Common } from 'src/domains/common/Common';
import { SingleBetItemState } from 'src/domains/sportsbook/betting/betSlipState/betsState/SingleBetItemState';
import { BetsState } from 'src/domains/sportsbook/betting/state/BetsState';
import { EventId, MarketId } from 'src_common/common/websocket2/id/WebsocketId';
import { ModelsState } from 'src_common/common/websocket2/ModelsState';

export class LegViewState {
    private readonly models: ModelsState;

    public constructor(
        public readonly common: Common,
        public readonly leg: SingleBetItemState['legs'][0],
        public readonly betsState: BetsState,
        public readonly betItem: SingleBetItemState
    ) {
        this.models = common.models;
    }

    @computed public get id(): string {
        return this.leg.id;
    }

    @computed public get selectionName(): string {
        return this.leg.selection?.name ?? '';
    }

    @computed public get isSettled(): boolean {
        return this.betItem.status === 'settled';
    }

    @computed public get isFreeBet(): boolean {
        return this.betItem.freeBetCredits.length > 0;
    }

    @computed public get eventId(): EventId | null {
        const rawEventId = this.leg.event.eventId ?? null;

        if (rawEventId !== null && rawEventId > 0) {
            return this.models.id.getEventIdOption(rawEventId);
        }

        const id = this.leg.event.id;

        if (typeof id === 'string') {
            const parsed = parseInt(id, 10);

            if (isNaN(parsed) || parsed <= 0) {
                return null;
            }

            return this.models.id.getEventIdOption(parsed);
        }

        if (typeof id === 'number' && id > 0) {
            return this.models.id.getEventIdOption(id);
        }

        return null;
    }

    @computed public get newMarketId(): MarketId | null {
        const marketId = this.leg.market?.id;

        if (this.eventId === null || marketId === null || marketId === undefined) {
            return null;
        }
        return this.eventId.getMarketId(marketId);
    }

    @computed public get numDenVal(): string | null {
        const terms = this.leg.eachWayTerms ?? null;
        if (terms === null) {
            return null;
        }

        return `${terms.reduction.num}/${terms.reduction.den}`;
    }

    @computed public get resultWinReduction(): string | null {
        const num = this.leg.result?.winReduction?.num ?? null;
        const den = this.leg.result?.winReduction?.den ?? null;

        if (num !== null && den !== null) {
            return `${num}/${den}`;
        }
        return null;
    }

    @computed public get price(): string | null {
        return this.betsState.getLegPrice(this.leg);
    }

    @computed public get spPrice(): string | null {
        return this.betsState.getLegPrice(this.leg, 'sp');
    }

    @computed public get isShowSPPrice(): boolean {
        const result = this.leg.result ?? null;

        return (
            this.leg.priceType === 'bp' &&
            this.betsState.isBogEnabled &&
            (this.betsState.selectedFilter === 'SETTLED' || this.betsState.selectedFilter === 'ALL') &&
            result !== null
        );
    }

    @computed public get isShowBogIndicator(): boolean {
        const result = this.leg.result ?? null;
        return (
            this.leg.priceType === 'bp' &&
            this.betsState.isBogEnabled &&
            this.betsState.isBogEligible(this.betItem.bet?.type) &&
            this.betsState.selectedFilter !== 'SETTLED' &&
            result === null &&
            this.isFreeBet === false
        );
    }
}
